import { useCallback, useEffect, useState } from 'react';
import { UserPlus, Check, X, FileText } from 'lucide-react';
import './Aanmeldingen.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const VIES = {
  geldig: { cls: 'ok', text: 'Btw geldig (VIES)' },
  ongeldig: { cls: 'bad', text: 'Btw ongeldig (VIES)' },
  fout: { cls: 'warn', text: 'VIES niet bereikbaar' },
};
const nl = (s) => { if (!s) return '—'; const [y, m, d] = String(s).slice(0, 10).split('-'); return `${d}-${m}-${y}`; };

// Mensen die zich via de inschrijvingslink hebben aangemeld en op akkoord wachten.
// Pas na Accorderen staan ze bij Medewerkers en in de planning.
export default function Aanmeldingen({ onAccepted, onCountChange }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    authFetch('/api/registratie/aanmeldingen').then((r) => (r.ok ? r.json() : [])).then((d) => {
      const arr = Array.isArray(d) ? d : [];
      setList(arr);
      onCountChange?.(arr.length);
    }).catch(() => {});
  }, [onCountChange]);
  useEffect(() => { load(); }, [load]);

  const act = async (w, actie) => {
    const naam = `${w.firstName} ${w.lastName}`;
    if (actie === 'afwijzen' && !window.confirm(`Aanmelding van ${naam} afwijzen?\n\nAlle gegevens en geüploade documenten (ook ID) worden definitief verwijderd.`)) return;
    setBusy(w.id); setErr('');
    try {
      const r = await authFetch(`/api/registratie/aanmeldingen/${w.id}/${actie}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Mislukt');
      if (actie === 'accorderen') onAccepted?.();
      load();
    } catch (e) {
      setErr(`${naam}: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  if (!list.length) return null;
  return (
    <section className="amd">
      <h3><UserPlus size={18} /> Nieuwe aanmeldingen <span className="amd-count">{list.length}</span></h3>
      <p className="amd-sub">Deze mensen hebben zich ingeschreven via de link. Controleer de gegevens en documenten. Na <strong>Accorderen</strong> staan ze bij Medewerkers en kun je ze een accountlink sturen.</p>
      {err && <p className="amd-err">{err}</p>}
      <ul className="amd-list">
        {list.map((w) => {
          const vies = VIES[w.viesStatus];
          const isOpen = open === w.id;
          return (
            <li key={w.id} className="amd-card">
              <div className="amd-top">
                <div className="amd-who">
                  <strong>{w.firstName} {w.lastName}</strong>
                  <span>{w.bedrijfsnaam || '—'} · {w.phone}</span>
                  <small>Aangemeld op {new Date(w.createdAt).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })}</small>
                </div>
                {vies && <span className={`amd-vies amd-vies--${vies.cls}`}>{vies.text}</span>}
              </div>

              <button type="button" className="amd-toggle" onClick={() => setOpen(isOpen ? null : w.id)} aria-expanded={isOpen}>
                {isOpen ? 'Gegevens verbergen' : 'Gegevens en documenten bekijken'}
              </button>

              {isOpen && (
                <div className="amd-detail">
                  <dl>
                    <div><dt>E-mail</dt><dd>{w.email || '—'}</dd></div>
                    <div><dt>Geboortedatum</dt><dd>{nl(w.dateOfBirth)}</dd></div>
                    <div><dt>Adres</dt><dd>{[w.address, w.postalCode, w.city].filter(Boolean).join(', ') || '—'}</dd></div>
                    <div><dt>KvK</dt><dd>{w.kvkNumber || '—'}</dd></div>
                    <div><dt>Btw</dt><dd>{w.btwNumber || '—'}</dd></div>
                  </dl>
                  {w.certificaten.length > 0 && (
                    <p className="amd-certs">{w.certificaten.map((c) => `${c.naam} (tot ${nl(c.geldigTot)})`).join(' · ')}</p>
                  )}
                  <div className="amd-files">
                    {w.files.map((f) => (
                      <a key={f.id} href={`/api/registratie/files/${f.id}`} target="_blank" rel="noopener noreferrer"><FileText size={14} /> {f.label}</a>
                    ))}
                  </div>
                </div>
              )}

              <div className="amd-actions">
                <button type="button" className="amd-btn amd-no" disabled={busy === w.id} onClick={() => act(w, 'afwijzen')}><X size={15} /> Afwijzen</button>
                <button type="button" className="amd-btn amd-yes" disabled={busy === w.id} onClick={() => act(w, 'accorderen')}><Check size={15} /> {busy === w.id ? 'Bezig…' : 'Accorderen'}</button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
